/* The demo gym generator, ported from acrostic-v1-app/app/js/seed.js (VERSION 35).
 *
 * SITE-RULES 7: the seed is GENERATED, not stored. 287 members and 48 leads out of this
 * file; the JSON equivalent is 607 KB plus a round trip.
 *
 * What changed in the port, and why — the rest is translation:
 *   · A member no longer carries visits[], notes[], scans[], interactions[], signals[]
 *     or followups[]. Those are EVENTS (MIGRATION.md §4). `anchors` stays on the member.
 *   · Ids are uuids, derived from a namespaced key so they are stable across builds.
 *   · One clock per build, so "two builds are byte-identical" is a property.
 *   · Campaigns, segments, tasks, monthly history, integrations and the activity feed
 *     are not ported: none has a type in the contract or a method on DataSource, and
 *     MIGRATION.md §6 defers or drops the screens that read them.
 *   · The two first-class joiners are chosen BEFORE the won-lead pairing rather than
 *     after, so the same person cannot be both "has not trained here yet" and "attended
 *     two trials". In the demo the two passes competed for the same newest joiners.
 */
import { SCORE_AMBER, } from '@acrostic/model';
/* THE ONE MODEL (MIGRATION.md §2). The seed does not carry a second implementation of
   the score, the anchor rules or the decay threshold: it imports them. seed.js had its
   own `riskModelSeed()` only because seed.js loaded before util.js in a script tag, and
   that constraint does not survive the rewrite. */
import { primaryAnchor } from '@acrostic/model/anchors';
import { detectRiskModel, riskModel } from '@acrostic/model/risk';
import { detectSources } from '@acrostic/model/scores';
import { FAT_PCT, PROTEIN_PER_LB, activityFor, computeMacros, macroGoalFor, macroSummary, toTargets, } from '@acrostic/model/macros';
import { isScored } from '@acrostic/model/segments';
import { MEMBER_TRACK } from '@acrostic/model/track';
import { Clock } from './clock.js';
import { classId as classIdFor, leadId as leadIdFor, memberId as memberIdFor, staffId, uuidFrom } from './ids.js';
import * as P from './pools.js';
/** Sending is off, so an active or draft campaign has sent nothing. Not a placeholder. */
const NOTHING_SENT = { sent: 0, opened: 0, clicked: 0, replied: 0, converted: 0 };
/** One completed campaign's history, from the one number that is real: how many it
 *  reached. Everything else is a share of that, so the funnel cannot widen as it goes. */
function funnel(sent, open, click, reply, conv) {
    return {
        sent,
        opened: Math.round(sent * open),
        clicked: Math.round(sent * click),
        replied: Math.round(sent * reply),
        converted: Math.round(sent * conv),
    };
}
import { Deal, SEED } from './rng.js';
const GYM_ID = uuidFrom('gym:your-gym');
/* A BIGGER RECORD COUNT IS A LONGER HISTORY, NOT A BIGGER GYM.
   Scaling every bucket by the same factor made ?members=2951 deal 2,622 ACTIVE members,
   which is not a boutique studio, it is a stadium. FNS has 2,951 records against 800+
   active across both studios: the other two thousand trained there once and left. A
   roster grows mostly at the cancelled end, because members leave and records do not. */
export const ACTIVE_SHARE_OF_GROWTH = 0.22;
export function scaleMix(mix, target) {
    const base = mix.reduce((a, x) => a + x[1], 0);
    const out = mix.map(([k, n]) => [k, n]);
    if (!(target > 0) || target === base)
        return out;
    if (target > base) {
        const growth = target - base;
        const extraActive = Math.round(growth * ACTIVE_SHARE_OF_GROWTH);
        const extraFormer = growth - extraActive;
        const activeBase = mix.filter((x) => x[0] !== 'cancelled').reduce((a, x) => a + x[1], 0);
        const ka = activeBase ? (activeBase + extraActive) / activeBase : 1;
        for (let j = 0; j < out.length; j++) {
            const row = out[j];
            row[1] = row[0] === 'cancelled' ? row[1] + extraFormer : Math.max(1, Math.round(row[1] * ka));
        }
    }
    else {
        const k = target / base;
        /* Every bucket keeps at least one: Paige Clark is rewritten over an at_risk member
           and the hold pass needs a frozen one, so neither may round to zero. */
        for (const row of out)
            row[1] = Math.max(1, Math.round(row[1] * k));
    }
    /* The remainder lands on the largest bucket. NEVER reorder: the deal order is the RNG
       sequence, and reordering it deals a different gym. */
    const diff = target - out.reduce((a, x) => a + x[1], 0);
    if (diff) {
        let big = 0;
        for (let j = 1; j < out.length; j++)
            if (out[j][1] > out[big][1])
                big = j;
        const row = out[big];
        row[1] = Math.max(1, row[1] + diff);
    }
    return out;
}
export function build(opts = {}) {
    const o = typeof opts === 'number' ? { members: opts } : opts;
    const want = Math.max(8, Math.round(o.members || P.DEFAULT_MEMBERS));
    const sizeK = want / P.DEFAULT_MEMBERS;
    const clock = new Clock(o.now ?? Date.now());
    const seed = o.seed ?? SEED;
    const d = Deal.fresh(seed); // fresh RNG each build: reproducible resets
    const daysAgo = (n) => clock.daysAgo(n);
    const ago = (x) => clock.ago(x);
    const since = (x) => clock.since(x);
    const today = clock.today();
    /* Faces are dealt in order from two pools, so a rebuild deals the same ones and no two
       people share a portrait. */
    const faceNext = { f: 0, m: 0 };
    const faceFor = (first) => {
        const g = P.FEMALE.has(first) ? 'f' : 'm';
        const n = faceNext[g]++ % P.FACE_POOL[g];
        return `assets/people/${g}-${n}.jpg`;
    };
    /* One name, one person. `taken` is every full name already dealt (members, then
       leads); the last name is re-picked until the pair is new. "Rosa Clark" twice on the
       Members list, on a product about knowing who somebody is, reads as a duplicate. */
    const taken = new Set();
    function uniqueName() {
        const first = d.pick(P.FIRST);
        let last = d.pick(P.LAST);
        let guard = 0;
        while (taken.has(first + ' ' + last) && guard++ < 40)
            last = d.pick(P.LAST);
        taken.add(first + ' ' + last);
        return { first, last };
    }
    /* Addresses people actually have: first.last, firstlast, first.l, f.last, and a birth
       year on some. The old first.last + a number from 1 to 99 printed "james.clark69". */
    function emailFor(first, last) {
        const ascii = (x) => x.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z]/g, '');
        const f = ascii(first);
        const l = ascii(last);
        const form = d.next();
        const local = form < 0.40 ? f + '.' + l
            : form < 0.62 ? f + l
                : form < 0.74 ? f + '.' + (l[0] ?? '')
                    : form < 0.86 ? (f[0] ?? '') + '.' + l
                        : f + l + d.pick(['82', '85', '88', '90', '91', '93', '94', '96', '97', '99', '01']);
        return local + '@' + d.pick(['gmail.com', 'gmail.com', 'gmail.com', 'yahoo.com', 'outlook.com', 'icloud.com', 'hotmail.com']);
    }
    function makeSource() {
        const cat = d.weighted(P.SOURCE_MIX, 'Website');
        let sub = '';
        if (cat === 'Funnel')
            sub = d.pick(P.FUNNEL_SUBS);
        else if (cat === 'Event')
            sub = d.pick(P.EVENT_NAMES);
        else if (cat === 'Website')
            sub = d.chance(0.15) ? 'Yelp' : '';
        else if (cat === 'Phone')
            sub = d.chance(0.25) ? 'Yelp' : '';
        return { source: cat, sourceDetail: sub };
    }
    /* Answered on about 60%; a third of those are a member's word of mouth, the most
       valuable answer there is, and it names the member. Class Pass has no option in the
       list, so it takes the free-text path the form offers. */
    function makeHeard(src, roster, selfId, friendShare) {
        if (!d.chance(0.60))
            return { heard: '', heardOther: '' };
        if (d.chance(friendShare == null ? 0.34 : friendShare)) {
            const pool = roster.filter((x) => x.id !== selfId);
            const other = pool.length ? d.pick(pool) : null;
            return { heard: 'Friend or member', heardOther: other ? other.name : '' };
        }
        if (src.source === 'Funnel') {
            const h = src.sourceDetail === 'Meta Ads' ? d.pick(['Facebook', 'Instagram'])
                : src.sourceDetail === 'Instagram' ? 'Instagram' : 'Google Search';
            return { heard: h, heardOther: '' };
        }
        if (src.source === 'Class Pass')
            return { heard: 'ClassPass app', heardOther: 'ClassPass app' };
        return { heard: d.pick(P.HEARD_BY_SOURCE[src.source] ?? ['Google Search']), heardOther: '' };
    }
    const pickPlan = () => d.weighted(P.PLANS.map((p) => [p, p.w]), P.PLANS[0]);
    const whyFor = (pool, first) => d.pick(P.FEMALE.has(first) ? pool : pool.filter((w) => !P.BODY_LINE.test(w)));
    /* ── The two intake assessments ───────────────────────────────────────────────
       Both tabs used to read "0 of N answered" for every member. Answered by about 55%
       (marketing) and 40% (fitness plan) so the "Fill in" state is still exercised.
       Medical and encrypted fields are left empty on purpose: those are not things a demo
       should invent. Answers agree with the rest of the record — the fitness goal follows
       the member's goal, the weight follows the latest InBody scan, the preferred time
       follows the home session. */
    function makeAssessments(archetype, goal, home, scans) {
        const out = {};
        const age = d.ri(22, 58);
        const pref = P.TIME_PREF[home.time] ?? P.TIME_PREF['6:00 AM'];
        if (d.chance(0.55)) {
            out['gender'] = d.pick(['Female', 'Female', 'Male', 'Male', 'Undisclosed']);
            out['maritalStatus'] = age < 28 ? d.pick(['Single', 'Single', 'Engaged']) : d.pick(['Single', 'Married', 'Married', 'Engaged']);
            out['demographic'] = age < 25 ? 'College (18-24)' : age <= 40 ? 'Young Professional (25-40)' : age <= 50 ? 'Working Professional (40-50)' : 'Professional (50+)';
            out['occupation'] = age < 24 ? d.pick(['Student', 'Student', 'Tech', 'Service (Police/Fire)']) : d.pick(P.OCCUPATIONS);
            out['children'] = age < 30 ? d.pick(['No Children', 'No Children', '1 Child']) : d.pick(['No Children', '1 Child', '2 Children', '2 Children', '3+ Children']);
            out['trainingBackground'] = archetype === 'committed'
                ? d.pick(['Consistent: training 3+ times a week', 'Athletic: competitive sport background', 'Some experience: training occasionally'])
                : d.pick(['New to structured training', 'Some experience: training occasionally', 'Some experience: training occasionally', 'Consistent: training 3+ times a week']);
            const city = d.pick(P.CITIES);
            out['address'] = { country: 'United States', street: d.ri(100, 4900) + ' ' + d.pick(P.STREETS), city: city[0], state: 'CA', postalCode: city[1] };
            out['preferredTime'] = pref[0];
            out['ageRange'] = age < 25 ? '18-24' : age < 35 ? '25-34' : age < 45 ? '35-44' : age < 55 ? '45-54' : '55-64';
            out['goalsInterest'] = d.someOf(['Personal Training', 'Small Group', 'Recovery', 'Fitness & Longevity', 'Team Training', 'Golf Performance'], 1, 3);
            out['primaryGoal'] = P.MKT_GOAL[goal] ?? 'Not sure yet';
            if (d.chance(0.4))
                out['anythingElse'] = d.pick(P.ANYTHING_ELSE);
        }
        if (d.chance(0.40)) {
            const last = scans.length ? scans[scans.length - 1] : null;
            const lbs = last?.weight ? Math.round(last.weight * 2.20462) : d.ri(120, 235);
            out['curWeight'] = lbs;
            out['curBodyfat'] = last ? String(last.pbf) : 'Unsure';
            const ft = d.pick([5, 5, 5, 5, 6]);
            out['heightFt'] = ft;
            out['heightIn'] = ft === 6 ? d.ri(0, 3) : d.ri(1, 11);
            out['fitnessGoal'] = P.AFP_GOAL[goal] ?? 'Be Healthier';
            out['whyIsThisGoalImportant'] = d.someOf(['Confidence', 'Health', 'Health', 'Family', 'Job'], 1, 2);
            out['finalGoalWeightLbs'] = goal === 'Fat loss' ? lbs - d.ri(8, 30) : goal === 'Build muscle' ? lbs + d.ri(4, 12) : lbs + d.ri(-5, 5);
            out['finalGoalBodyFat'] = last?.pbf != null ? String(Math.max(8, Math.round(last.pbf - (goal === 'Fat loss' ? d.ri(4, 9) : d.ri(0, 3))))) : 'Unsure';
            out['goal12Months'] = d.pick(P.GOAL_12M);
            out['goalNext100Days'] = d.pick(P.GOAL_100D);
            out['jobActivityLevel'] = d.pick(['Sedentary', 'Sedentary', 'Light Activity', 'Light Activity', 'Active', 'Very Active']);
            out['exerciseExperience'] = d.someOf(['Cardio experience', 'Gym circuit machines experience', 'Strength Training with free weights', 'Cross Functional Training Experience', 'No Exercise experience'], 1, 3);
            out['preferredWorkoutTime'] = d.chance(0.3) ? pref[1] + ';Only Weekends' : pref[1];
            out['goalBlockers'] = d.someOf(['Accountability/Motivation', 'Time to workout', 'Nutrition', 'Consistency', 'Traveling', 'Mindset', 'Education on Exercise Form/Technique'], 1, 3);
            out['diet'] = d.pick(['Non-Vegetarian', 'Non-Vegetarian', 'Non-Vegetarian', 'Vegetarian', 'Flexitarian', 'Vegan', 'Pollotarian']);
            out['nutritionHelp'] = d.chance(0.2) ? 'None' : d.someOf(['Meal Portions', 'Macros', 'Recipes - Balanced Meals', 'Cravings'], 1, 2);
            const partner = d.pick(['None', 'Spouse/Partner', 'Spouse/Partner', 'Friend', 'Friend']);
            out['accountabilityPartner'] = partner;
            out['accountabilityPartnerName'] = partner === 'None' ? '' : d.pick(P.FIRST);
            out['averageSleepHours'] = d.pick(['5-6', '6', '6-7', '7', '7-8', '8']);
            out['dailyWaterOunces'] = d.ri(4, 12) * 8;
            out['homeEquipment'] = d.chance(0.35) ? 'None' : d.someOf(['Dumbells', 'Medicine ball', 'Pilates ball', 'Bosu Ball'], 1, 2);
            out['homeRecoveryTools'] = d.chance(0.3) ? 'None' : d.someOf(['Foam Roller', 'Massage gun', 'Resistance Bands', 'Therapy ball'], 1, 2);
        }
        return out;
    }
    /* ── One member ─────────────────────────────────────────────────────────────── */
    function makeMember(i, archetype) {
        const { first, last } = uniqueName();
        /* A trial member is on a trial week, not on a paid plan at $0. pickPlan() is still
           drawn so the RNG sequence holds. */
        const drawn = pickPlan();
        const plan = archetype === 'trial' ? { name: 'Trial week', price: 0 } : drawn;
        let legacyStatus = 'Active';
        let att = 0, pay = 0, eng = 0, lastVisitDays = 0, visitsWk = 0, joinedDays = 0;
        let priorWk = null;
        switch (archetype) {
            case 'committed':
                att = d.ri(78, 100);
                pay = d.ri(90, 100);
                eng = d.ri(65, 100);
                visitsWk = d.rf(2.6, 4.6);
                lastVisitDays = d.ri(0, 3);
                joinedDays = d.ri(180, 1800);
                priorWk = visitsWk + d.rf(-0.4, 0.4);
                break;
            case 'steady':
                att = d.ri(58, 82);
                pay = d.ri(85, 100);
                eng = d.ri(45, 80);
                visitsWk = d.rf(1.6, 2.8);
                lastVisitDays = d.ri(0, 7);
                joinedDays = d.ri(120, 1400);
                priorWk = visitsWk + d.rf(-0.5, 0.5);
                break;
            case 'fading':
                att = d.ri(32, 55);
                pay = d.ri(70, 100);
                eng = d.ri(25, 55);
                visitsWk = d.rf(0.5, 1.5);
                lastVisitDays = d.ri(4, 16);
                joinedDays = d.ri(90, 900);
                priorWk = d.rf(2.0, 3.8);
                break;
            case 'at_risk':
                att = d.ri(5, 30);
                pay = d.ri(40, 95);
                eng = d.ri(5, 35);
                visitsWk = d.rf(0, 0.6);
                lastVisitDays = d.ri(14, 45);
                joinedDays = d.ri(90, 700);
                priorWk = d.rf(1.5, 3.2);
                break;
            /* STILL PAYING, NOT SEEN SINCE LAST WINTER. Active, past onboarding, months
               since the last visit — and the direct debit still going out. `priorWk` is
               set because they were once a regular: that is what makes them a different
               problem from somebody who never started, and it is what the why line reads. */
            case 'dormant':
                att = d.ri(0, 12);
                pay = d.ri(75, 100);
                eng = d.ri(0, 18);
                visitsWk = 0;
                lastVisitDays = d.ri(70, 400);
                priorWk = d.rf(1.4, 3.0);
                joinedDays = lastVisitDays + d.ri(150, 1200);
                break;
            case 'new':
                att = d.ri(60, 95);
                pay = 100;
                eng = d.ri(55, 95);
                visitsWk = d.rf(2, 4);
                lastVisitDays = d.ri(0, 4);
                joinedDays = d.ri(5, 85);
                break;
            case 'trial':
                att = d.ri(50, 90);
                pay = 100;
                eng = d.ri(50, 95);
                visitsWk = d.rf(1.5, 3);
                lastVisitDays = d.ri(0, 5);
                joinedDays = d.ri(1, 13);
                break;
            case 'frozen':
                legacyStatus = 'Suspended';
                att = 0;
                pay = d.ri(60, 100);
                eng = d.ri(10, 40);
                visitsWk = 0;
                lastVisitDays = d.ri(20, 90);
                joinedDays = d.ri(200, 1500);
                break;
            case 'cancelled':
                legacyStatus = d.pick(['Terminated', 'Terminated', 'Terminated', 'Expired', 'Expired', 'Declined']);
                att = 0;
                pay = d.ri(20, 80);
                eng = d.ri(0, 20);
                visitsWk = 0;
                lastVisitDays = d.ri(45, 400);
                joinedDays = d.ri(300, 2500);
                break;
        }
        const live = legacyStatus === 'Active';
        const status = live ? 'active' : legacyStatus === 'Suspended' ? 'suspended' : 'cancelled';
        const health = Math.round(att * 0.5 + pay * 0.3 + eng * 0.2);
        const goal = d.pick(P.GOALS);
        const joined = daysAgo(joinedDays);
        // InBody scan history (~72% of non-trial members have scans)
        const scans = [];
        if (archetype !== 'trial' && d.chance(0.72)) {
            const n = Math.min(d.ri(3, 10), Math.max(2, Math.floor(joinedDays / 30)));
            let weight = d.rf(52, 108);
            let pbf = goal === 'Build muscle' ? d.rf(12, 22) : d.rf(20, 38);
            let smm = weight * d.rf(0.26, 0.34);
            for (let k = n - 1; k >= 0; k--) {
                const drift = (archetype === 'committed' || archetype === 'new') ? 1 : (archetype === 'steady' ? 0.6 : 0.15);
                scans.push({
                    date: daysAgo(Math.min(joinedDays - 2, 14 + k * 30 + d.ri(-4, 4))),
                    weight: +weight.toFixed(1), pbf: +pbf.toFixed(1), smm: +smm.toFixed(1), score: d.ri(58, 88),
                });
                if (goal === 'Fat loss') {
                    weight += d.rf(0.2, 1.1) * drift;
                    pbf += d.rf(0.2, 0.9) * drift;
                    smm -= d.rf(0, 0.25) * drift;
                }
                else if (goal === 'Build muscle') {
                    weight -= d.rf(0.1, 0.6) * drift;
                    smm -= d.rf(0.15, 0.5) * drift;
                    pbf += d.rf(0, 0.4) * drift;
                }
                else {
                    weight += d.rf(-0.4, 0.5);
                    pbf += d.rf(-0.3, 0.4);
                    smm -= d.rf(-0.2, 0.2);
                }
            }
            scans.sort((a, b) => (a.date < b.date ? -1 : 1));
            for (const s of scans) {
                s.weight = +Math.max(45, s.weight ?? 0).toFixed(1);
                s.pbf = +Math.min(48, Math.max(8, s.pbf ?? 0)).toFixed(1);
                s.smm = +Math.max(16, s.smm ?? 0).toFixed(1);
            }
        }
        /* PEOPLE HAVE A HOME SESSION, and a session has ONE coach. Picking each visit's
           class at random gave every class ~220 "regulars" out of 255 active members, and
           made "who trains with Coach Marcus J." unanswerable. The home session is 68% of
           visits, a second one 20%, anything else the rest. */
        const coached = P.SCHEDULE.map((s, idx) => ({ s, idx })).filter((x) => /^Coach /.test(x.s.instructor));
        const home = d.pick(coached);
        const second = d.chance(0.45) ? d.pickOr(coached.filter((x) => x.idx !== home.idx), home) : null;
        const pickSession = () => {
            const r = d.next();
            if (r < 0.68)
                return home.idx;
            if (second && r < 0.88)
                return second.idx;
            return d.ri(0, P.SCHEDULE.length - 1);
        };
        /* A visit cannot predate the join. The window is the last 30 days OR the whole
           membership, whichever is shorter. It OPENS with the last visit rather than a fixed
           30 days: clamping every visit into the last month meant nobody could be away for
           longer than 30 days, so the at-risk archetype's 14-to-45-day absence was capped
           and "Overdue" (no visit in 30+ days) was always zero. */
        const span = Math.min(joinedDays, lastVisitDays + 30);
        lastVisitDays = Math.min(lastVisitDays, span);
        const visits = [];
        if (live) {
            const total = Math.round((visitsWk * 4.3 * span) / 30);
            /* NOBODY ATTENDS THE SAME SESSION TWICE ON THE SAME DAY. The pairs are drawn at
               random, so without this they collided constantly: 168 of 261 members carried a
               duplicate, 346 in all. It is not cosmetic. A duplicate inflates visits30d and
               health, which are inputs to the number the whole product ranks people by, so
               the roster was ordered partly by a dice roll. It is also the exact thing
               check-in idempotency exists to prevent: the seed was breaking the rule the app
               enforces.
      
               A collision is skipped rather than redrawn forever, so a member can end up with
               slightly fewer visits than the target. That is the honest outcome: you cannot
               attend a class you already attended. */
            const seen = new Set();
            const offer = (v) => {
                const k = v.date + '|' + v.session;
                if (seen.has(k))
                    return false;
                seen.add(k);
                visits.push(v);
                return true;
            };
            for (let k = 0; k < total; k++) {
                let tries = 0;
                while (!offer({ date: daysAgo(d.ri(Math.min(lastVisitDays + 1, span), span)), session: pickSession() }) && ++tries < 6) {
                    /* redraw a few times before giving the slot up */
                }
            }
            /* The most recent visit is ALWAYS the home session. The member's coach derives
               from it, which is what makes "assigned to a coach" mean the coach this person
               actually trains with. If the random pass already dealt that exact session on
               that day, the existing one IS this visit. */
            offer({ date: daysAgo(lastVisitDays), session: home.idx });
            visits.sort((a, b) => (a.date < b.date ? 1 : -1));
        }
        const homeSession = P.SCHEDULE[home.idx];
        const assess = makeAssessments(archetype, goal, homeSession, scans);
        /* Two to four, oldest first. A TIMELINE: notes are never removed, only added, so the
           count scales with tenure — a four-year member has forty-odd and a joiner has two.
           A NOTE CANNOT PREDATE THE JOIN: `Math.max(6, joinedDays)` let a member who joined
           three days ago carry notes dated six days back, which the unified timeline renders
           below the "before they joined" seam — the gym writing about somebody who was not
           a member yet. */
        const notes = (() => {
            const bank = (P.MEMBER_NOTES[archetype] ?? []).concat(P.MEMBER_NOTES['any'] ?? []);
            const n = archetype === 'trial' ? 1 : Math.max(2, Math.min(48, Math.round(joinedDays / d.ri(22, 34))));
            const out = [];
            const used = new Set();
            for (let k = 0; k < n; k++) {
                let t = d.pick(bank);
                let guard = 0;
                while (used.has(t) && guard++ < 12)
                    t = d.pick(bank);
                if (used.size >= bank.length - 1)
                    used.clear();
                used.add(t);
                out.push({ text: t, who: d.pick(P.SUPPORT_ROSTER), at: daysAgo(d.ri(0, Math.max(1, joinedDays))) });
            }
            return out.sort((a, b) => (a.at < b.at ? -1 : 1));
        })();
        /* Roughly a third of members brought somebody in. Wired after the roster exists. */
        const wantsReferral = d.chance(0.34);
        const id = memberIdFor(i);
        /* A PT-plan member is somebody's client, and the trainer is the instructor of the
           session they mostly train in: the coach they already know. Only while the
           membership is live — a cancelled PT client is not a client. */
        const isPT = live && /\bPT\b/.test(plan.name);
        const lastVisit = live ? daysAgo(lastVisitDays) : (visits[0]?.date ?? daysAgo(lastVisitDays));
        const expires = live ? daysAgo(-d.ri(20, 300)) : null;
        const spend = Math.round((plan.price || 0) * Math.max(1, Math.round(joinedDays / 30)));
        const sessionsLeft = /Unlimited/.test(plan.name) ? 0 : d.ri(2, 11);
        const mbId = 'MB-' + d.ri(100000, 999999);
        const ibId = scans.length ? 'IB-' + d.ri(10000, 99999) : undefined;
        const m = {
            id, gymId: GYM_ID,
            firstName: first, lastName: last, name: first + ' ' + last,
            email: emailFor(first, last),
            phone: '(408) 555-' + String(d.ri(1000, 9999)),
            // No photo yet for some of the newest people: the upload on Edit details is for them.
            photoUrl: ((archetype === 'new' && d.chance(0.4)) || (archetype === 'trial' && d.chance(0.5))) ? null : faceFor(first),
            status,
            joinedAt: joined,
            cancelledAt: archetype === 'cancelled' ? daysAgo(d.ri(10, 300)) : null,
            plan: plan.name,
            priceCents: plan.price * 100,
            membershipExpires: expires,
            pt: /\bPT\b/i.test(plan.name),
            goal,
            why: d.chance(0.70) ? whyFor(P.WHY, first) : null,
            source: null, sourceDetail: null, // filled by makeSource below, in deal order
            supportCoachId: null, coachId: null, supportCoach: null, coach: null,
            suppression: null, cancellation: null,
            anchors: [],
            primaryAnchor: null,
            health,
            lastVisit,
            visits30d: 0, // counted off the events once the roster is final
            avgVisitsWk: +visitsWk.toFixed(1),
            /* The rate they held before this stretch. It prints the drop as this person's own
               line ("Was 3 a week, now 1") instead of one sentence for everyone. */
            priorVisitsWk: priorWk != null ? +Math.max(0, priorWk).toFixed(1) : null,
            risk: null,
            fromLead: null,
            firstClass: null,
            /* Omitted when absent, not set to undefined. A member with no InBody id has no
               inbodyId key at all, which is what the optional property means and what
               exactOptionalPropertyTypes enforces. */
            externalIds: { mindbodyId: mbId, ...(ibId ? { inbodyId: ibId } : {}) },
            sf: {
                legacyStatus,
                healthParts: { attendance: att, payments: pay, engagement: eng },
                related: {
                    items: {
                        n: 1 + (joinedDays > 365 ? 1 : 0),
                        unlimited: /Unlimited/.test(plan.name) ? 1 : 0,
                        sessionsLeft, spend, expires,
                    },
                    referrals: { n: 0 },
                },
                referrals: [],
                wantsReferral,
                ...assess,
            },
            tags: isPT ? ['pt'] : [],
        };
        const src = makeSource();
        m.source = src.source;
        m.sourceDetail = src.sourceDetail || null;
        /* The coach is the instructor of the home session. `coach` is the display name and
           `coachId` the stable staff id, so the Coach seat resolves its own roster. */
        m.coach = /^Coach /.test(homeSession.instructor) ? homeSession.instructor : null;
        m.coachId = m.coach ? staffId(m.coach) : null;
        return { m, archetype, joinedDays, home: home.idx, legacyStatus, visits, notes, scans, touches: [], signal: null, followUp: null };
    }
    /* ── Deal the roster ──────────────────────────────────────────────────────── */
    const drafts = [];
    {
        let i = 0;
        for (const [arch, n] of scaleMix(P.MEMBER_MIX, want)) {
            for (let k = 0; k < n; k++)
                drafts.push(makeMember(i++, arch));
        }
    }
    const members = drafts.map((x) => x.m);
    const activeMembers = () => members.filter((m) => m.status === 'active');
    /* ── Anchor pass ──────────────────────────────────────────────────────────────
       Runs after the roster exists, because a member-to-member anchor needs somebody to
       point at. Anchor counts are deliberately correlated with archetype: committed
       members are known by two or three people, at-risk members by almost nobody. That
       correlation is the product thesis, so the demo shows it rather than asserting it. */
    function anchorCount(arch) {
        switch (arch) {
            case 'committed': return d.ri(2, 3);
            case 'steady': return d.chance(0.78) ? d.ri(1, 2) : 0;
            case 'new': return d.chance(0.40) ? 1 : 0; // the gym has not built it yet
            case 'trial': return d.chance(0.25) ? 1 : 0;
            case 'fading': return d.chance(0.45) ? 1 : 0;
            case 'at_risk': return d.chance(0.15) ? 1 : 0; // zero anchors IS the signal
            /* They had one once. Nobody has spoken to them in months, so it has decayed —
               which is the honest picture, and why they are not a phone call this week. */
            case 'dormant': return d.chance(0.45) ? 1 : 0;
            case 'frozen': return d.chance(0.60) ? 1 : 0;
            case 'cancelled': return d.chance(0.20) ? 1 : 0;
            default: return 0;
        }
    }
    {
        const active = activeMembers();
        for (const x of drafts) {
            const m = x.m;
            const n = anchorCount(x.archetype);
            if (n) {
                const lastSeen = since(m.lastVisit);
                const used = new Set();
                for (let k = 0; k < n; k++) {
                    // First anchor is staff; a second or third may be a training partner.
                    const asPartner = k > 0 && d.chance(0.45);
                    let name, role, basis;
                    let otherMemberId;
                    if (asPartner) {
                        const buddy = active.length ? d.pick(active) : null;
                        if (!buddy || buddy.id === m.id || used.has(buddy.name))
                            continue;
                        name = buddy.name;
                        role = 'Member';
                        basis = d.pick(P.PARTNER_BASIS);
                        otherMemberId = buddy.id;
                    }
                    else {
                        const staff = d.chance(0.55) ? d.pick(P.SUPPORT_ROSTER) : (d.chance(0.75) ? d.pick(P.COACHES) : d.pick(P.FRONT_DESK));
                        if (used.has(staff))
                            continue;
                        name = staff;
                        role = P.SUPPORT_ROSTER.includes(staff) ? 'Support coach'
                            : P.COACHES.includes(staff) ? 'Coach'
                                // Nina is the Manager login; Cole is the one person still described by
                                // where they stand rather than a role key.
                                : staff === 'Nina Alvarez' ? 'Manager' : 'Front desk';
                        basis = d.pick(P.ANCHOR_BASIS);
                    }
                    used.add(name);
                    // An anchor cannot have been two-way more recently than the member was last here.
                    const gap = lastSeen + d.ri(0, 24);
                    const anchor = {
                        id: uuidFrom('anchor:' + m.id + ':' + name),
                        name, role, basis,
                        kind: asPartner ? 'member' : 'staff',
                        since: daysAgo(Math.max(14, Math.min(since(m.joinedAt) - 7, d.ri(30, 900)))),
                        lastTwoWay: daysAgo(gap),
                        inherited: false,
                    };
                    if (asPartner && otherMemberId)
                        anchor.otherMemberId = otherMemberId;
                    else
                        anchor.staffId = staffId(name);
                    m.anchors.push(anchor);
                }
            }
            /* INHERITED ANCHORS. A member who converted from a lead keeps the support coach
               who ran their trials: the relationship carries across the join, even though the
               90-day onboarding clock restarts at zero on the join date. `since` therefore
               predates joinedAt, which is the whole point — the record can say "known here
               since before they were a member". */
            const joinedAgo = since(m.joinedAt);
            if (m.joinedAt && joinedAgo <= 400) {
                const carried = m.anchors.find((a) => a.role === 'Support coach');
                if (carried && d.chance(0.65)) {
                    carried.inherited = true;
                    carried.since = daysAgo(joinedAgo + d.ri(6, 34)); // met them while still a lead
                    carried.basis = d.pick(P.INHERITED_BASIS);
                }
            }
            m.primaryAnchor = primaryAnchor(m, today);
        }
    }
    /* EVERY MEMBER IS ASSIGNED A SUPPORT COACH. Assignment is not an anchor: if a support
       coach already knows them, that person is the assignment; otherwise the desk shares
       them out, and the member is "assigned to Dana, unknown to Dana" — which is exactly
       the gap this product is about. Its own pass because the anchor loop `continue`s on
       members with no anchors, and those are the ones this must not skip. */
    for (const m of members) {
        const known = m.anchors.find((a) => a.role === 'Support coach');
        m.supportCoach = known ? known.name : d.pick(P.SUPPORT_ROSTER);
        m.supportCoachId = staffId(m.supportCoach);
    }
    /* ── Paige Clark ──────────────────────────────────────────────────────────────
       The member the landing page tells the story about, so the demo can open the same
       person: 38 days since anyone saw them, assigned to a coach, known by nobody. One
       at-risk member is rewritten IN PLACE rather than added, so the roster count, the RNG
       sequence and every other face stay exactly as they were. Nothing here is a number
       the model does not derive: the score falls out of the absence, the empty anchor list
       and the health signals like anyone else's. */
    {
        const p = drafts.find((x) => x.archetype === 'at_risk' && x.m.status === 'active');
        const clash = members.find((m) => m !== p?.m && m.name === 'Paige Clark');
        if (clash) {
            clash.lastName = 'Hayes';
            clash.name = clash.firstName + ' Hayes';
        }
        if (p) {
            const homeIdx = P.SCHEDULE.findIndex((x) => x.name === 'Signature 45' && /Marcus/.test(x.instructor));
            const home = homeIdx >= 0 ? homeIdx : 0;
            const session = P.SCHEDULE[home];
            Object.assign(p.m, {
                firstName: 'Paige', lastName: 'Clark', name: 'Paige Clark',
                email: 'paige.clark@gmail.com', phone: '(408) 555-0138', photoUrl: faceFor('Paige'),
                status: 'active',
                plan: 'Unlimited Membership', priceCents: 39900,
                joinedAt: daysAgo(430), cancelledAt: null,
                goal: 'General fitness',
                why: 'Wants to be the one who says yes to the hike, not the one who waits in the car.',
                health: 34,
                lastVisit: daysAgo(38), visits30d: 0, avgVisitsWk: 0.3, priorVisitsWk: 3.1,
                anchors: [], primaryAnchor: null, suppression: null, cancellation: null,
                supportCoach: 'Coach Dana R.', supportCoachId: staffId('Coach Dana R.'),
                coach: session.instructor, coachId: staffId(session.instructor),
                tags: [],
            });
            p.m.sf['legacyStatus'] = 'Active';
            p.m.sf['healthParts'] = { attendance: 8, payments: 92, engagement: 12 };
            p.legacyStatus = 'Active';
            p.joinedDays = 430;
            p.home = home;
            p.scans = [];
            p.visits = [38, 45, 52, 59, 66, 73, 80].map((n) => ({ date: daysAgo(n), session: home }));
            p.notes = [
                { text: 'Knee grumbling after the Tuesday session. Scaled the lunges, no complaints after.', who: 'Coach Marcus J.', at: daysAgo(66) },
                { text: 'Said the 6:00a is getting hard with the school run. Mentioned the lunch class.', who: 'Coach Marcus J.', at: daysAgo(45) },
            ];
        }
    }
    /* Referral rows, as their OWN pass. This used to sit at the end of the anchor loop,
       which `continue`s for members with no anchors, so those members kept an unfilled
       placeholder and their Referrals card stayed broken. */
    {
        const active = activeMembers();
        for (const m of members) {
            const sf = m.sf;
            if (!sf['wantsReferral']) {
                delete sf['wantsReferral'];
                continue;
            }
            delete sf['wantsReferral'];
            const pool = active.filter((x) => x.id !== m.id);
            if (!pool.length)
                continue;
            const other = d.pick(pool);
            const made = d.chance(0.5);
            const when = daysAgo(Math.max(4, Math.min(since(m.joinedAt) - 2, d.ri(20, 420))));
            sf['referrals'] = [{
                    date: when, refNo: 'REF-' + d.ri(400, 999),
                    direction: made ? 'made' : 'received',
                    otherMemberId: other.id,
                    giftCardIssued: d.chance(0.6), giftCardBlocked: false,
                    supportCoach: m.primaryAnchor?.name ?? d.pick(P.SUPPORT_ROSTER),
                    id: 'MR-' + d.ri(700, 999),
                }];
            sf['related'].referrals = { n: 1, made: made ? 1 : 0, received: made ? 0 : 1 };
        }
    }
    /* "How did you hear about us?" on the member's marketing assessment, for the members
       who answered it. A member with a RECEIVED referral on file heard about the place
       from that person, so the two answers agree; everyone else follows the source they
       came through. Its own pass because it names other members. */
    {
        const active = activeMembers();
        for (const m of members) {
            const sf = m.sf;
            if (!sf['occupation'])
                continue;
            const rows = sf['referrals'] ?? [];
            const got = rows.find((r) => r.direction === 'received' && r.otherMemberId);
            const ref = got ? members.find((x) => x.id === got.otherMemberId) : null;
            // A lower share here than on leads: the received-referral rule already names one.
            const h = ref ? { heard: 'Friend or member', heardOther: ref.name } : makeHeard({ source: m.source ?? 'Website', sourceDetail: m.sourceDetail ?? '' }, active, m.id, 0.20);
            if (!h.heard)
                continue;
            sf['heardAboutUs'] = h.heard;
            if (h.heardOther)
                sf['heardAboutUsOther'] = h.heardOther;
        }
    }
    /* ── Care Track & Hold pass ───────────────────────────────────────────────────
       Suppression is a safety feature, not a filter. Anyone carrying `suppression` is OFF
       the risk model: no score, no ranking, no automated message. Frozen members are the
       obvious population, but the important cases are the ACTIVE ones — a member training
       through a bereavement still shows up, and scoring them is exactly the harm this
       exists to prevent.
  
       A HOLD is for someone who is actually away, so it lands only on the frozen archetype
       (suspended, no visits): an active member training three times a week was "away".
       Active members get Care Track only. Declared by the member's own support coach: that
       is whose book they are on, so this runs after the support-coach pass. */
    for (const x of drafts) {
        const m = x.m;
        let kind = null;
        if (x.archetype === 'frozen')
            kind = d.chance(0.45) ? 'care' : 'hold';
        else if (m.status === 'active' && d.chance(0.035))
            kind = 'care';
        if (!kind)
            continue;
        /* You cannot declare Care Track on somebody who is not a member yet. Unclamped,
           a recent joiner could carry a declaration dated before their own join, which the
           timeline renders above the enquiry that started them. */
        const tenure = Math.max(1, since(m.joinedAt));
        if (kind === 'care') {
            const declared = Math.min(d.ri(6, 70), tenure);
            m.suppression = {
                kind,
                reason: whyFor(P.CARE_REASONS, m.firstName),
                declaredAt: daysAgo(declared),
                declaredBy: m.supportCoach ?? d.pick(P.SUPPORT_ROSTER),
                // A review date is what stops suppression becoming abandonment.
                reviewAt: daysAgo(declared - d.ri(30, 90)),
            };
        }
        else {
            /* The review date falls inside the span the reason states, counted from the day
               the hold was declared; the hold was declared recently enough that the review is
               at most about a week overdue. */
            const hold = d.pick(P.HOLD_REASONS);
            const spanDays = d.ri(hold.weeks[0], hold.weeks[1]) * 7;
            const declared = Math.min(d.ri(6, Math.min(70, spanDays + 7)), tenure);
            m.suppression = {
                kind,
                reason: hold.text,
                declaredAt: daysAgo(declared),
                declaredBy: m.supportCoach ?? d.pick(P.SUPPORT_ROSTER),
                reviewAt: daysAgo(declared - spanDays),
            };
        }
    }
    /* ONE HOLD ENDING THE DAY AFTER TOMORROW, on purpose.
     *
     * The consoles prompt two days before a hold ends — ask how the knee is, rather
     * than let the membership silently resume (Anuj, 2026-09-15). Left to the dice,
     * three holds land their review dates weeks out and the panel is empty on every
     * seat, every day: a feature the demo contains and never shows.
     *
     * So the FIRST hold, in roster order, is moved to today + 2. First rather than
     * random because the seed is deterministic and has to stay that way — the same
     * roster must deal the same gym twice, and `d` is not consulted here at all.
     * Everyone else keeps the date the dice gave them. */
    {
        const firstHold = drafts.find((x) => x.m.suppression?.kind === 'hold');
        if (firstHold?.m.suppression)
            firstHold.m.suppression.reviewAt = daysAgo(-2);
    }
    /* ── Leads ────────────────────────────────────────────────────────────────────
       Pipeline sized to a realistic studio (41 open), scaled with the roster: a gym ten
       times the size is working ten times the pipeline. */
    const leads = [];
    {
        const active = activeMembers();
        let li = 0;
        for (const [stage, n] of scaleMix(P.LEAD_MIX, Math.round(48 * sizeK))) {
            for (let k = 0; k < n; k++) {
                const { first, last } = uniqueName(); // no lead shares a name with a member or a lead
                const plan = pickPlan();
                const created = daysAgo(stage === 'new' ? d.ri(0, 4) : stage === 'contacted' ? d.ri(2, 10) : d.ri(5, 28));
                const owner = d.pick(P.SUPPORT_ROSTER);
                const touched = daysAgo(d.ri(0, 6));
                const src = makeSource();
                const heard = makeHeard(src, active, null);
                const why = whyFor(P.LEAD_WHY, first);
                const id = leadIdFor(li++);
                const createdAgo = ago(created) ?? 0;
                /* Dated stage transitions. A lead's `stage` is only where they are NOW, which
                   makes every question about movement unanswerable: how many were at trial a
                   month ago, whether the board is filling or draining. The walk they actually
                   took is known (it is the same walk the notes are written against), so it is
                   recorded with dates, and the lifecycle strip's trend becomes real arithmetic
                   rather than a decoration. */
                const path = ['contacted', 'prospect_1', 'prospect_2', 'nurture', 'won', 'lost'];
                const upto = path.indexOf(stage);
                const walked = upto === -1 ? []
                    : (stage === 'won' || stage === 'lost') ? ['contacted', 'prospect_1', stage]
                        : path.slice(0, upto + 1);
                const atStep = (i) => daysAgo(Math.max(0, Math.round(createdAgo * (1 - (i + 1) / (walked.length + 1)))));
                const stageHistory = [{ stage: 'new', at: created }];
                walked.forEach((st, i) => stageHistory.push({ stage: st, at: atStep(i) }));
                /* A HISTORY, not one line. With a single note the Ownership card ("what the last
                   conversation was about") and the Recent notes card printed the same sentence
                   twice, side by side. A lead at prospect_2 has been spoken to more than once. */
                const notes = [];
                walked.forEach((st, i) => {
                    const bank = P.LEAD_NOTES[st];
                    if (!bank)
                        return;
                    notes.push({ at: atStep(i), who: owner, text: d.pick(bank) });
                });
                const basis = P.LEAD_BASIS[stage];
                const lead = {
                    id, gymId: GYM_ID,
                    memberId: null,
                    firstName: first, lastName: last, name: first + ' ' + last,
                    email: emailFor(first, last),
                    phone: '(408) 555-' + String(d.ri(1000, 9999)),
                    // Half of brand-new enquiries have no photo yet; most people further along do.
                    photoUrl: (stage === 'new' ? d.chance(0.5) : d.chance(0.15)) ? null : faceFor(first),
                    stage: stage,
                    stageHistory,
                    /* The owner and the anchor are the SAME person. Without that the record
                       contradicted itself: the anchor panel named a support coach while the
                       Ownership card underneath said "nobody covers this prospect". */
                    owner, ownerId: staffId(owner),
                    interest: plan.name,
                    valueCents: plan.price * 100,
                    source: src.source, sourceDetail: src.sourceDetail || null,
                    heard: heard.heard || null,
                    why, goal: d.pick(P.GOALS),
                    createdAt: created,
                    lastActivity: daysAgo(Math.max(0, d.ri(0, 3))),
                    nextAction: P.NEXT_ACTION[stage] ?? null,
                    /* A lost lead ALWAYS carries a reason here. The product refuses to close
                       one without it, so a seed that dealt a reasonless lost lead would be
                       dealing a record the product could not have produced. */
                    lostReason: stage === 'lost' ? d.pick(P.DROPOUT_REASONS) : null,
                    lostReasonDetail: null,
                    /* A `new` lead has NO anchor, on purpose: an anchor is reciprocal, and a form
                       submission is not a relationship. */
                    anchor: basis ? {
                        id: uuidFrom('anchor:' + id + ':' + owner),
                        name: owner, role: 'Support coach', kind: 'staff', staffId: staffId(owner),
                        basis, since: created, lastTwoWay: touched, inherited: false,
                    } : null,
                    notes,
                };
                leads.push(lead);
            }
        }
    }
    /* ── Today's timetable ──────────────────────────────────────────────────────── */
    const classes = P.SCHEDULE.map((c, i) => ({
        id: classIdFor(i), gymId: GYM_ID, name: c.name, time: c.time,
        instructor: /^Coach /.test(c.instructor) ? c.instructor : null,
        instructorId: /^Coach /.test(c.instructor) ? staffId(c.instructor) : null,
        cap: c.cap,
    }));
    /* ── Campaigns ────────────────────────────────────────────────────────────────
       Each one NAMES A REAL SEGMENT, and the audience is sized from segmentMembers() at
       read time, so the number on the Campaigns screen is the number on the Segments
       screen for the same people. Until 2026-09-04 the at-risk save carried a hard-coded
       7 against a rule that matched no segment, and the two screens disagreed about who
       would be reached.
  
       The two completed ones carry history; the rest carry zeros, because sending is off
       and a screen that says so must not also show an open rate. */
    const campaigns = [
        {
            id: uuidFrom('campaign:1'), gymId: GYM_ID, name: '7-Day Lead Nurture',
            type: 'nurture', channel: 'email + sms', status: 'active',
            segmentId: null, createdAt: daysAgo(85),
            subject: 'Your first session at {{gym_name}} is waiting, {{first_name}}',
            body: 'Hi {{first_name}}, great goals start with one session. Your trial class is on us this week. Reply YES and we will lock in your spot with a coach who trains for {{goal}}.',
            stats: NOTHING_SENT,
        },
        {
            id: uuidFrom('campaign:2'), gymId: GYM_ID, name: 'At-Risk Member Save',
            type: 'retention', channel: 'sms', status: 'active',
            segmentId: 's1', createdAt: daysAgo(41),
            subject: '',
            body: 'Hey {{first_name}}, we noticed you have missed a couple of weeks. Totally normal, life happens. {{coach}} saved you a spot in {{class}} this week. One session gets the rhythm back. In?',
            stats: NOTHING_SENT,
        },
        {
            id: uuidFrom('campaign:3'), gymId: GYM_ID, name: 'Win-Back: 90-Day Lapsed',
            type: 'winback', channel: 'email', status: 'completed',
            segmentId: 's6', createdAt: daysAgo(60),
            subject: 'We rebuilt the program you left, {{first_name}}',
            body: 'It has been a few months, {{first_name}}. Members on the new small-group format are averaging 3.1 sessions a week. Your old rate is available until Friday. Want it?',
            stats: funnel(Math.round(members.filter((m) => m.status === 'cancelled').length * 0.9), 0.52, 0.2, 0.11, 0.07),
        },
        {
            id: uuidFrom('campaign:4'), gymId: GYM_ID, name: 'Summer Strong Challenge',
            type: 'announcement', channel: 'email + sms', status: 'completed',
            segmentId: null, createdAt: daysAgo(24),
            subject: '6 weeks. One team. Summer Strong starts July 14.',
            body: '{{first_name}}, the Summer Strong Challenge kicks off July 14: two InBody scans, a weekly leaderboard, prizes from our retail partners. Registration closes Friday. Grab your spot at the front desk.',
            stats: funnel(Math.round(members.filter((m) => m.status === 'active' && !m.suppression).length * 0.94), 0.8, 0.49, 0.26, 0.36),
        },
        {
            id: uuidFrom('campaign:5'), gymId: GYM_ID, name: 'July Referral Push',
            type: 'referral', channel: 'sms', status: 'draft',
            segmentId: 's4', createdAt: daysAgo(2),
            subject: '',
            body: '{{first_name}}, you have done {{visits_30d}} sessions this month. Know somebody who would thrive here? Gift them a free week. If they join, you both get $50 off. Reply with their number and we will handle the rest.',
            stats: NOTHING_SENT,
        },
    ];
    /* ── Approvals ────────────────────────────────────────────────────────────────
       The things a Support Coach genuinely cannot do alone: a price or plan change, a
       cancellation, a duplicate merge, and a lead written off. Every one points at a REAL
       record in this seed — an approval about a member who does not exist is the same lie
       as a fake number. `payload.patch` is what approving APPLIES; `payload.fields` is
       what the queue DISPLAYS. */
    const approvals = (() => {
        const out = [];
        const askers = ['Coach Dana R.', 'Coach Marcus L.', 'Coach Priya S.'];
        const actives = activeMembers();
        if (!actives.length)
            return out;
        const pickM = (i) => actives[(i * 37 + 11) % actives.length];
        const priced = pickM(1);
        const oldPrice = (priced.priceCents ?? 0) / 100;
        out.push({
            id: uuidFrom('approval:1'), gymId: GYM_ID, kind: 'member_update',
            memberId: priced.id,
            summary: 'Price $' + oldPrice + ' → $' + (oldPrice - 50) + '/mo',
            requestedBy: askers[0], requestedAt: daysAgo(1),
            payload: {
                targetName: priced.name,
                fields: { price: '$' + oldPrice + ' → $' + (oldPrice - 50) },
                patch: { priceCents: (oldPrice - 50) * 100 },
                reason: 'Retention discount agreed on the floor',
            },
            decision: null,
        });
        const cancelling = pickM(2);
        out.push({
            id: uuidFrom('approval:2'), gymId: GYM_ID, kind: 'member_update',
            memberId: cancelling.id,
            summary: 'Cancel membership',
            requestedBy: askers[1], requestedAt: daysAgo(1),
            payload: {
                targetName: cancelling.name,
                fields: { status: 'Active → Cancelled' },
                patch: { status: 'cancelled' },
                reason: 'Moving out of state at the end of the month',
            },
            decision: null,
        });
        const held = pickM(3);
        const foundations = P.PLANS.find((x) => x.name === 'Foundations (6-wk)');
        out.push({
            id: uuidFrom('approval:3'), gymId: GYM_ID, kind: 'member_update',
            memberId: held.id,
            summary: 'Plan ' + held.plan + ' → Foundations (6-wk)',
            requestedBy: askers[2], requestedAt: daysAgo(2),
            payload: {
                targetName: held.name,
                fields: { plan: held.plan + ' → Foundations (6-wk)' },
                // A plan change is a price change: the new plan's price, not the old one kept.
                patch: { plan: foundations.name, priceCents: foundations.price * 100 },
                reason: 'Stepping down after injury, wants to keep coming',
            },
            decision: null,
        });
        const dupe = pickM(4);
        out.push({
            id: uuidFrom('approval:4'), gymId: GYM_ID, kind: 'merge',
            memberId: dupe.id,
            summary: 'Merge duplicate record',
            requestedBy: askers[0], requestedAt: daysAgo(3),
            /* No patch on purpose. The seed does not carry the duplicate row: a second record
               for the same person would sit on the Members list and in the counts as a real
               member until somebody approved this. */
            payload: { targetName: dupe.name, fields: {}, reason: 'Two records from the Mindbody import, same phone number' },
            decision: null,
        });
        const lapsing = leads.find((l) => l.stage === 'nurture');
        if (lapsing) {
            out.push({
                id: uuidFrom('approval:5'), gymId: GYM_ID, kind: 'lead_update',
                leadId: lapsing.id,
                summary: 'Write off as lost: no reply in 45 days',
                requestedBy: askers[1], requestedAt: daysAgo(4),
                payload: {
                    targetName: lapsing.name,
                    fields: { stage: 'lost', lostReason: 'No reply in 45 days' },
                    patch: { stage: 'lost' },
                    reason: 'Proposed after 45 days of silence',
                },
                decision: null,
            });
        }
        return out;
    })();
    /* ══ CONNECT PASS ══════════════════════════════════════════════════════════
       Everything here exists so the unified timeline has something real to merge. It runs
       last because it needs the finished roster on both sides. Nothing is invented that
       the product could not know: an interaction is a call or a message a named person
       made, a signal is the risk model firing on a date, a follow-up is a row on the
       pipeline with what actually happened after it.
  
       Suppressed members get NONE of it. They are not chased. ═══════════════════ */
    /* ── The member who has not trained yet ───────────────────────────────────────
       Every active member in the seed had at least one visit, so "First class" could
       never fire on a register: the one row where member context is worth the most, the
       person nobody in the room has met, did not exist. Two joiners are rewound to their
       induction: they signed up in the last day or two and their first class is today.
  
       This is NOT a bookings table. It is the one thing a gym does know about somebody
       who has not been yet: which session they are inducting into. Rosters otherwise
       still derive from attendance. They are inside their first 90 days, so not scored.
  
       PORT CHANGE: this runs BEFORE the won-lead pairing, and the two it picks are
       excluded from the joiner pool. In the demo both passes competed for the newest
       joiners, so the same person could be "has not trained here yet" and also carry two
       trial classes they had attended. */
    const firstTimers = new Set();
    {
        const fresh = drafts
            .filter((x) => x.m.status === 'active' && x.archetype === 'new' && !x.m.suppression)
            .sort((a, b) => String(b.m.joinedAt).localeCompare(String(a.m.joinedAt)))
            .slice(0, 2);
        fresh.forEach((x, i) => {
            const c = classes[i % classes.length];
            if (!c)
                return;
            const m = x.m;
            m.joinedAt = daysAgo(i); // signed up today, and yesterday
            m.lastVisit = null;
            m.visits30d = 0;
            m.avgVisitsWk = 0;
            m.priorVisitsWk = null;
            x.joinedDays = i;
            x.visits = []; // they have not trained here yet
            x.scans = [];
            x.notes = [{
                    text: 'Joined at the desk. Booked in for their first class.',
                    who: m.supportCoach ?? d.pick(P.SUPPORT_ROSTER),
                    at: m.joinedAt,
                }];
            /* The TIME as well as the name: the timetable runs Signature 45 twice a day, and
               matching on the name alone put the same person on both registers. */
            m.firstClass = {
                date: clock.today(), className: c.name, time: c.time,
                ...(c.instructor ? { coach: c.instructor } : {}),
            };
            /* Their coach is the one taking the class they are inducting into. There is no
               attendance to derive it from yet, and this is the one thing the gym does know. */
            m.coach = c.instructor;
            m.coachId = c.instructorId;
            firstTimers.add(m.id);
        });
    }
    /* ── Lead → member continuity (SITE-RULES 11c) ────────────────────────────────
       A won lead is the SAME PERSON as a member, so the member carries the enquiry
       forward: original source, the trials they ran, who ran them and every note from
       before they joined. No second record, no history lost. */
    {
        const wonLeads = leads.filter((l) => l.stage === 'won');
        const joiners = drafts
            /* Not a trial-week member either: a won lead has done both trials and signed,
               so pairing one with somebody on "Trial week" put "Signed up on the spot after
               the second trial" above a plan that says they have not signed yet. */
            .filter((x) => x.m.status === 'active' && x.archetype !== 'trial'
            && !firstTimers.has(x.m.id) && !x.m.fromLead && (since(x.m.joinedAt) <= 120))
            .sort((a, b) => String(b.m.joinedAt).localeCompare(String(a.m.joinedAt)));
        wonLeads.forEach((l, i) => {
            const x = joiners[i];
            if (!x)
                return;
            const m = x.m;
            l.memberId = m.id;
            /* THE ENQUIRY DATES ARE DERIVED FROM THE JOIN, never carried across as-is. A lead
               record is created on its own clock, so pairing it with a member straight up
               produced impossible histories: an enquiry dated after the day the person had
               already joined, and trial classes they attended before they existed. The
               enquiry opens 14 to 45 days before they joined and everything in between is
               spaced inside that window. */
            const joinAgo = since(m.joinedAt);
            const spanDays = d.ri(14, 45);
            const openAgo = joinAgo + spanDays;
            l.createdAt = daysAgo(openAgo);
            const steps = Math.max(1, l.stageHistory.length - 1);
            l.stageHistory = l.stageHistory.map((s, k) => ({
                stage: s.stage,
                // The last transition (won) lands ON the join date: that IS the day they joined.
                at: k === 0 ? l.createdAt : daysAgo(Math.round(openAgo - spanDays * (k / steps))),
            }));
            const notes = l.notes;
            const carried = notes.map((nn, k) => ({
                at: daysAgo(Math.round(openAgo - spanDays * ((k + 1) / (notes.length + 1)))),
                who: nn.who, text: nn.text,
            }));
            m.fromLead = {
                id: l.id,
                createdAt: l.createdAt,
                source: l.source, sourceDetail: l.sourceDetail,
                heard: l.heard, why: l.why,
                owner: l.owner,
                interest: l.interest,
                notes: carried,
                // Both trials sit inside the window, the second one nearer the join.
                trials: [
                    { at: daysAgo(Math.round(openAgo - spanDays * 0.45)), name: 'Trial 1', coach: l.owner },
                    { at: daysAgo(Math.round(openAgo - spanDays * 0.75)), name: 'Trial 2', coach: l.owner },
                ],
            };
            /* The lead's own notes move onto the derived clock too: one person, one story,
               and the lead record must not still be dated off a clock nothing else uses. */
            l.notes = carried;
            // The enquiry's source is the member's source. One person, one origin.
            m.source = l.source;
            m.sourceDetail = l.sourceDetail;
        });
    }
    /* ── Interactions, with their source and their owner ──────────────────────── */
    for (const x of drafts) {
        const m = x.m;
        if (m.suppression)
            continue; // rule 1: nobody is chased on Care Track
        if (m.status !== 'active')
            continue;
        /* PORT CHANGE: the demo drew this from `ri(1, max(8, min(180, tenure)))`, whose
           8-day floor dated a call up to three days before a five-day-old member joined.
           Same defect as the note rule, one pool over. A member who joined today has had
           no conversations here yet. */
        const window = Math.min(180, since(m.joinedAt));
        if (window < 1)
            continue;
        const who = m.supportCoach ?? d.pick(P.SUPPORT_ROSTER);
        const n = x.archetype === 'fading' || x.archetype === 'at_risk' ? d.ri(2, 4)
            : x.archetype === 'dormant' ? d.ri(0, 1)
                : x.archetype === 'committed' ? d.ri(1, 3) : d.ri(0, 2);
        const out = [];
        for (let k = 0; k < n; k++) {
            const r = d.next();
            const kind = r < 0.4 ? 'call' : r < 0.72 ? 'sms' : 'note';
            const bank = kind === 'call' ? P.CALL_LINES : kind === 'sms' ? P.SMS_LINES : P.INPERSON_LINES;
            out.push({
                at: daysAgo(d.ri(1, window)),
                kind: kind === 'note' ? 'call' : kind,
                text: d.pick(bank),
                by: who,
                source: kind === 'sms' ? 'Message' : 'Staff',
            });
        }
        /* THE MEMBER ANSWERS SOME OF THEM (PRD 6). Without this the thread is the gym
           talking to itself, which would make the Conversation tab a send log and say
           the product broadcasts. Not all of them: silence is a real outcome and the
           ones that went unanswered are exactly what the ladder escalates on. */
        for (const t of [...out]) {
            if (t.kind !== 'sms' || !d.chance(0.62))
                continue;
            const lag = d.ri(0, 2);
            const at = daysAgo(Math.max(0, since(t.at) - lag));
            out.push({
                at, kind: 'sms', text: d.pick(P.SMS_REPLIES),
                by: m.name, source: 'Member', inbound: true,
            });
        }
        x.touches = out.sort((a, b) => (a.at < b.at ? -1 : 1));
    }
    /* Which model this TENANT can honestly run. Asked ONCE for the gym, not once per
       member: a synced tenant has no anchors on anybody, and scoring that naively opens
       the whole roster in Red. The seeded gym does model anchors, so this answers 'full'
       — but it is asked rather than assumed. */
    const tenantModel = detectRiskModel(members);
    /* ASKED ONCE FOR THE GYM, per input (PRD 14), and it cannot be asked yet: the
       evidence pass below is what puts the signals on the members, and asking before
       it runs answers "this gym records nothing", which is how the first wiring of
       this marked all twenty-two inputs unsourced and quietly undid the pass. It is
       assigned immediately after, and nothing reads it before then. */
    let tenantUnsourced = [];
    /* `scoreOf` is a closure and is not called until after the evidence pass below
       has run, which is what lets it read tenantUnsourced before that is assigned.
       Declared with `let` and filled in after the evidence exists - see the comment
       where it is. */
    /* isScored() is the ONE place that knows who may hold a number: suppressed, frozen,
       former, cancelling, and inside the first 90 days all answer no. It used to ask
       about suppression and cancellation only, which is how a first-90-days member ended
       up with a stored score that every screen was then trusted to hide. */
    const scoreOf = (m) => isScored(m, today)
        ? riskModel(m, { today, model: tenantModel, unsourced: tenantUnsourced }).score
        : null;
    /* ── The first 90 days, actually run (PRD 20) ─────────────────────────────────
       Evidence for the five OWED touches, so the first-90 screen shows a gym that
       runs the programme rather than one that does nothing.
  
       Without this pass every onboarding member reads "4 of the five owed touches
       have not happened", because nothing in the seed ever recorded one. That is
       technically honest - the screen only counts evidence - and it is useless: a
       list where everybody is overdue is a list nobody can triage, and it argues
       that the product's own programme is unrunnable.
  
       So the dice decide how well each member has been run, and the mix is the
       point. Most are kept up, a fifth have slipped one touch, and a tenth have
       genuinely been dropped. `d` is the seeded rng, so the same roster deals the
       same gym twice.
  
       THE TOUCH IS DATED A DAY OR TWO LATE ON PURPOSE. A gym that hits every
       checkpoint on its exact day is a gym running software, not a gym. */
    {
        const OWED = MEMBER_TRACK.filter((c) => c.milestone);
        const WORDS = {
            1: 'Welcome and a tour. Introduced them to their support coach and to one other member.',
            7: 'First week check. Asked how the first sessions felt and what is getting in the way.',
            21: 'Programme review. Placed them into a standing class so they see the same faces.',
            45: 'How is it fitting into your week? Moved their usual slot to something that sticks.',
            90: 'Day 90 review: counted who actually knows them here.',
        };
        for (const x of drafts) {
            const days = x.joinedDays;
            if (x.m.status === 'cancelled' || days == null || days > 90)
                continue;
            /* How well this member has been run. Kept deliberately coarse: three kinds
               of gym rather than a continuous dial nobody could read off the screen. */
            const roll = d.next();
            const keptUpTo = roll < 0.68 ? 90 // run properly
                : roll < 0.88 ? 21 // slipped after the first weeks
                    : 7; // dropped early
            for (const cp of OWED) {
                if (cp.at > days)
                    break; // not owed yet
                if (cp.at > keptUpTo)
                    break; // owed, and not done
                const late = d.ri(0, 2);
                const at = daysAgo(Math.max(0, days - cp.at - late));
                x.notes.push({
                    text: WORDS[cp.at] ?? 'Check-in.',
                    who: cp.owner === 'coach' ? (x.m.coach ?? d.pick(P.SUPPORT_ROSTER))
                        : cp.owner === 'manager' ? 'Nina Alvarez'
                            : cp.owner === 'gym_admin' ? 'Simone Okafor'
                                : (x.m.supportCoach ?? d.pick(P.SUPPORT_ROSTER)),
                    at,
                });
            }
        }
    }
    /* ── The evidence the five scores read (PRD 4) ────────────────────────────
       Counts and dates derived from what the seed has already built, plus the few
       the demo has to invent because no earlier pass produced them.
  
       WHY THIS PASS EXISTS AT ALL. The five scores ask more of a gym than the three
       families did: not just "are they coming and does anybody know them" but "what
       has the gym actually DONE for them". Without this the demo could answer three
       of the five, and the two it could not - how well we serve them, and community
       - are the two the product is most original about. A demo that cannot show
       them is a demo arguing for a different product.
  
       DERIVED WHERE POSSIBLE, INVENTED ONLY WHERE NECESSARY, and the difference is
       marked. Derived numbers move with the rest of the gym when the seed changes;
       invented ones are dice and have to be shaped deliberately so the roster does
       not read as uniformly good or uniformly neglected. */
    {
        const within = (iso, days) => iso != null && since(iso) <= days;
        for (const x of drafts) {
            const m = x.m;
            if (m.status === 'cancelled')
                continue;
            /* — DERIVED from passes that already ran — */
            m.contacts90d = x.touches.filter((t) => !t.inbound && within(t.at, 90)).length;
            m.conversation90d = x.touches.filter((t) => t.kind === 'sms' && within(t.at, 90)).length;
            /* Human facts are what staff wrote down about the person. The seed's notes
               are exactly that: "brings his own coffee", "knee is holding up". */
            m.humanFacts = x.notes.length;
            /* FOLLOW-UPS ARE NOT BUILT YET at this point in the build - they are two
               hundred lines further down - so they are topped up after they exist. Read
               here they were always null, which made "follow-ups closed with an outcome"
               read zero for every member and therefore UNSOURCED for the whole gym: the
               discipline metric PRD 8 cares most about, silently absent from the demo. */
            const scans = x.scans;
            m.lastScanAt = scans.length ? scans[scans.length - 1].date : null;
            /* MOVING TOWARD THE GOAL, not simply changing. Fat loss wants the number
               down and muscle wants it up, so the direction is read against what they
               came for. Null with fewer than two scans: one scan is a photograph and
               has no direction to read. */
            if (scans.length >= 2) {
                const first = scans[0];
                const last = scans[scans.length - 1];
                const goal = String(m.goal ?? '');
                m.scanSeriesMoving = /muscle/i.test(goal)
                    ? (last.smm ?? 0) >= (first.smm ?? 0)
                    : (last.pbf ?? 0) <= (first.pbf ?? 0);
            }
            else {
                m.scanSeriesMoving = null;
            }
            /* — INVENTED, because no earlier pass produces them — */
            /* A GOAL IS ONLY WORTH ANYTHING WHILE IT IS CURRENT. Most gyms review on a
               six-month cycle and let a proportion slip, which is the finding the score
               exists to surface: "an out-of-date goal is worse than no goal, because it
               makes the gym think it knows". */
            m.goalReviewDueAt = m.goal
                ? daysAgo(d.chance(0.3) ? d.ri(5, 90) : -d.ri(10, 170))
                : null;
            /* Recognition, referrals and guests are the community half. Kept sparse on
               purpose: these are the things a gym does rarely and remembers, and a
               roster where everybody has been recognised says nothing. */
            m.recognitions = d.chance(0.22) ? d.ri(1, 2) : 0;
            m.referralsMade = d.chance(0.18) ? d.ri(1, 3) : 0;
            m.guestsBrought = d.chance(0.12) ? 1 : 0;
            /* Events: invited from the model, turnout recorded. A gym invites BROADLY -
               that is what an invite list built from the model is for - so most members
               have a turnout to read. Leaving 45% uninvited made community unanswerable
               for half the roster and the score meaningless for the other half. */
            if (d.chance(0.85)) {
                const invited = d.ri(1, 3);
                m.eventsInvited = invited;
                /* TURNOUT IS THE COMMON CASE, not the exception. The first cut had half of
                   the invited members attend nothing and the rest attend a random share,
                   which averaged about a quarter and made community read 18 across the
                   roster - a gym nobody ever comes to. Most people who are asked to
                   something at their own gym go to at least one. The ones who skip
                   everything are the finding, and PRD 10 says so: "a member who skips
                   three invitations in a row is told something is off before their
                   attendance shows it". */
                m.eventsAttended = d.chance(0.72) ? d.ri(1, invited) : 0;
            }
            else {
                m.eventsInvited = 0;
                m.eventsAttended = 0;
            }
            /* The nutrition engine has not been built (SCOPE B3), so targets and plans
               exist for the members a coach has actually worked through. Dated against
               the scan so the staleness rule has something to catch. */
            if (m.lastScanAt && d.chance(0.45)) {
                const stale = d.chance(0.35);
                m.macrosAt = stale ? daysAgo(since(m.lastScanAt) + d.ri(20, 90)) : m.lastScanAt;
                m.mealPlanAt = d.chance(0.7) ? m.macrosAt : null;
                /* THE TARGETS THEMSELVES, so the Macros tab has something to show. Computed
                   by the one calculator from the scan that was current when they were set:
                   a stale set is worked from an older scan and reads as older. No draws
                   from `d` here on purpose - one extra draw reshuffles every member built
                   after this one, and the score distribution with them. */
                const at = m.macrosAt;
                const basis = [...scans].reverse().find((s) => s.date <= at) ?? null;
                const latest = scans[scans.length - 1];
                const w = basis?.weight ?? (latest.weight != null ? +(latest.weight * 1.02).toFixed(1) : null);
                const bf = basis?.pbf ?? (latest.pbf != null ? +(latest.pbf + 1).toFixed(1) : null);
                if (w != null) {
                    const inputs = {
                        weightKg: w, bodyFatPct: bf ?? null,
                        activity: activityFor(m.visits30d), goal: macroGoalFor(m.goal),
                        proteinPerLb: PROTEIN_PER_LB.default, fatPct: FAT_PCT.default,
                    };
                    const res = computeMacros(inputs);
                    m.macros = 'missing' in res ? null
                        : toTargets(inputs, res, { setAt: at, by: m.supportCoach, scanDate: basis?.date ?? null });
                }
                else {
                    m.macros = null;
                }
            }
            else {
                m.macrosAt = null;
                m.mealPlanAt = null;
                m.macros = null;
            }
            m.assessmentAt = d.chance(0.4) ? daysAgo(d.ri(30, 400)) : null;
            /* A COMPLAINT IS RARE AND MATTERS ENORMOUSLY. Four or five on a roster of
               287, and some of them past the seven-day line, because a complaint nobody
               closed is one of the two signals PRD 4 weights heavily on its own. */
            m.complaintOpenedAt = d.chance(0.02) ? daysAgo(d.ri(2, 30)) : null;
            /* NO SOURCE AT ALL, and left that way deliberately. Recovery practice and
               wearables arrive with the member app in phase 2, and inventing them here
               would have the demo claim a capability that does not exist. The record
               says "no source today" and names what would fill it, which is the honest
               answer and is also the PRD's. */
            m.recoveryLogged90d = null;
        }
    }
    /* ── Cancellation initiated (PRD 4) ───────────────────────────────────────────
       Three people, and WHO they are is the whole reason this is seeded by hand
       rather than by dice.
  
       The state exists because a member who asks about cancelling is the most urgent
       person in the building AND IS FREQUENTLY GREEN WHEN THEY ASK: they have been
       training all along and something outside the gym changed. Left to the dice this
       lands on three drifting members, the panel agrees with the risk model, and the
       demo shows a feature that appears to do nothing.
  
       So: the first is the BEST-SCORING active member on the roster, the second has
       been waiting nine days, and the third is already on Care Track, which is the
       ordering case the PRD leaves open. Deterministic - `d` is not consulted here,
       because the same roster must deal the same gym twice. */
    {
        const eligible = drafts
            .filter((x) => x.m.status === 'active' && !x.m.cancellation)
            .map((x) => ({
            x,
            score: x.m.suppression ? -1
                : riskModel(x.m, { today, model: tenantModel, unsourced: tenantUnsourced }).score,
        }));
        /* THE GREEN ONE. Highest score on the roster, so the panel is demonstrably not
           a second at-risk list: the person pinned to the top of the Owner's console is
           the one the model likes most. */
        const green = eligible.filter((e) => e.score >= 0).sort((a, b) => b.score - a.score)[0];
        if (green) {
            green.x.m.cancellation = {
                askedAt: daysAgo(2),
                asked: 'Asked what the notice period is, at the desk after class',
                recordedBy: green.x.m.supportCoach ?? 'Front desk',
                resolvedAt: null,
                resolution: null,
            };
        }
        /* THE ONE WHO HAS BEEN WAITING. Nine days with no reply is what the panel is
           sorted to surface, and it ages visibly rather than expiring. */
        const waiting = eligible.find((e) => e.x !== green?.x && e.score >= 0);
        if (waiting) {
            waiting.x.m.cancellation = {
                askedAt: daysAgo(9),
                asked: 'Emailed to ask how to cancel before the next payment',
                recordedBy: 'Front desk',
                resolvedAt: null,
                resolution: null,
            };
        }
        /* THE ORDERING CASE. Already on Care Track, and this outranks it: away is not
           gone, but asking to leave is more urgent than either. */
        const suppressed = drafts.find((x) => x.m.suppression?.kind === 'care' && !x.m.cancellation);
        if (suppressed) {
            suppressed.m.cancellation = {
                askedAt: daysAgo(4),
                asked: 'Asked whether to cancel rather than keep paying while not training',
                recordedBy: suppressed.m.supportCoach ?? 'Front desk',
                resolvedAt: null,
                resolution: null,
            };
        }
    }
    /* ── The risk signal as an event, not just a number ───────────────────────────
       The score dropping is something that HAPPENED on a day. Without it the timeline
       cannot answer "what changed before they went quiet". The threshold is SCORE_AMBER
       from shared, and the score is the product's own: seed.js carried a stand-in
       (`riskModelSeed`) only because it loaded before util.js. */
    for (const x of drafts) {
        const m = x.m;
        /* NOT FOR ANYBODY WHO HOLDS NO SCORE. "Member score moved to 64" on a member in
           their first 90 days is the divergence showing up as history rather than as a
           number, and it would survive on the timeline after the number itself went. */
        if (!isScored(m, today) || m.status !== 'active')
            continue;
        const r = scoreOf(m);
        if (r == null || r > SCORE_AMBER)
            continue;
        const dsv = m.lastVisit ? since(m.lastVisit) : null;
        x.signal = {
            at: daysAgo(Math.max(1, (dsv ?? 21) - 14)),
            title: 'Risk signal',
            detail: (dsv ? 'No visit in ' + dsv + ' days' : 'No visit on record') + '. Member score moved to ' + r + '.',
        };
    }
    /* ── The follow-up pipeline, with an OUTCOME (SITE-RULES 11d) ─────────────────
       The existing pipeline recorded that an action was due. It never recorded what came
       back, so nothing could learn. Each row now carries why the person is on it, what
       happened, the recommended action, an owner, a due date and — once it has been
       worked — the outcome. */
    const followUps = [];
    for (const x of drafts) {
        const m = x.m;
        if (m.suppression || m.status !== 'active')
            continue; // rule 1, again and on purpose
        const r = scoreOf(m);
        if (r == null || r > SCORE_AMBER)
            continue;
        const dsv = m.lastVisit ? since(m.lastVisit) : 30;
        const worked = d.chance(0.55);
        const o = d.pick(P.OUTCOMES);
        const at = daysAgo(Math.max(1, Math.min(20, dsv - 7)));
        const owner = m.supportCoach ?? d.pick(P.SUPPORT_ROSTER);
        const f = {
            id: uuidFrom('followup:member:' + m.id),
            memberId: m.id,
            why: dsv > 14 ? 'No visit in ' + dsv + ' days' : 'Attendance dropping',
            happened: m.lastVisit ? 'Last visit ' + m.lastVisit : 'No visit on record',
            action: dsv > 21 ? 'Reach out to check in' : 'Check in and re-engage',
            owner, ownerId: staffId(owner),
            due: daysAgo(d.ri(-3, 4)),
            outcome: worked ? o.outcome : null,
            outcomeDetail: worked ? o.detail : null,
            outcomeAt: worked ? daysAgo(Math.max(0, (ago(at) ?? 0) - d.ri(1, 4))) : null,
            status: worked ? o.status : 'pending',
        };
        x.followUp = f;
        followUps.push(f);
    }
    /* Leads get the same treatment: a follow-up is a follow-up whoever it is on. */
    const leadFollowUps = new Map();
    for (const l of leads) {
        if (l.stage === 'won' || l.stage === 'lost' || l.stage === 'new')
            continue;
        const worked = d.chance(0.5);
        const o = d.pick(P.OUTCOMES);
        const notes = l.notes;
        const f = {
            id: uuidFrom('followup:lead:' + l.id),
            leadId: l.id,
            why: l.stage === 'prospect_2' ? 'Completed both trials' : l.stage === 'prospect_1' ? 'Completed first trial' : 'Enquiry not yet booked',
            happened: notes.length ? notes[notes.length - 1].text : 'No contact yet',
            action: l.nextAction ?? 'Follow up',
            owner: l.owner ?? 'Coach Dana R.', ...(l.ownerId ? { ownerId: l.ownerId } : {}),
            due: daysAgo(d.ri(-3, 5)),
            outcome: worked ? o.outcome : null,
            outcomeDetail: worked ? o.detail : null,
            outcomeAt: worked ? daysAgo(d.ri(0, 3)) : null,
            status: worked ? o.status : 'pending',
        };
        leadFollowUps.set(l.id, f);
        followUps.push(f);
    }
    /* ── Follow-up evidence, now that follow-ups exist ────────────────────────
       "A follow-up is complete when the outcome is written down, not when the
       message leaves", so only the ones closed WITH an outcome count. Counted off
       the real list rather than the one draft field, which also picks up the second
       set of follow-ups that never hung off a draft at all. */
    {
        const closed = new Map();
        const overdue = new Map();
        for (const f of followUps) {
            if (!f.memberId)
                continue;
            const done = f.status === 'completed' && f.outcome != null
                && f.outcomeAt != null && since(f.outcomeAt) <= 90;
            const late = f.status !== 'completed' && f.due < today;
            if (done)
                closed.set(f.memberId, (closed.get(f.memberId) ?? 0) + 1);
            if (late)
                overdue.set(f.memberId, (overdue.get(f.memberId) ?? 0) + 1);
        }
        for (const m of members) {
            if (m.status === 'cancelled')
                continue;
            m.outcomesClosed90d = closed.get(m.id) ?? 0;
            m.followUpsOverdue = overdue.get(m.id) ?? 0;
        }
    }
    /* AND ASK THE GYM HERE, with every signal finally on the members. Asked any
       earlier and the answer is "this gym records nothing", which is how the first
       wiring of this marked all twenty-two inputs unsourced. */
    tenantUnsourced = detectSources(members.filter((m) => m.status !== 'cancelled'), { today });
    /* ── Emit the event stream ────────────────────────────────────────────────────
       One pass, at the very end, over the FINAL roster. Running it here rather than as
       each pass writes is what makes "a note can never predate the join" hold even for
       the two joiners the first-class pass rewinds to today. */
    const events = [];
    const rank = {
        lead_created: 0, contacted: 1, trial_booked: 2, trial_attended: 3, converted: 4,
        membership: 5, note: 6, attendance: 7, inbody: 8, call: 9, sms: 10, email: 11,
        signal: 12, followup: 13, outcome: 14, care: 15, missed: 16, cancelled: 17,
    };
    for (const x of drafts) {
        const m = x.m;
        const own = [];
        const push = (e) => own.push({ payload: {}, ...e, id: '', memberId: m.id, leadId: null });
        /* The enquiry, unfolded onto the member. The record does not begin mid-story. */
        const fl = m.fromLead;
        if (fl) {
            push({
                at: fl.createdAt, kind: 'lead_created', title: 'Enquiry',
                detail: [fl.source, fl.sourceDetail].filter(Boolean).join(' · ') + (fl.why ? ' · ' + fl.why : ''),
                who: fl.owner, source: 'Enquiry', tone: 'info', icon: 'spark',
            });
            for (const t of fl.trials) {
                push({
                    at: t.at, kind: 'trial_attended', title: t.name, detail: 'Trial class with ' + (t.coach ?? 'a coach'),
                    who: t.coach, source: 'Register', tone: 'good', icon: 'check',
                });
            }
            /* Source 'Enquiry', not 'Staff'. These notes are MEANT to predate the join — the
               record does not begin mid-story (SITE-RULES 11c) — and the seam is what lets
               the timeline say "before they joined" and the rule "a member's notes never
               predate their join date" stay checkable on the notes it is about. */
            for (const nn of fl.notes) {
                push({ at: nn.at, kind: 'note', title: 'Note', detail: nn.text, who: nn.who, source: 'Enquiry', tone: 'plain', icon: 'note', payload: { preJoin: true } });
            }
        }
        if (m.joinedAt) {
            push({
                at: m.joinedAt,
                kind: fl ? 'converted' : 'membership',
                title: fl ? 'Joined, from the enquiry' : 'Joined',
                detail: m.plan ?? 'Membership',
                who: m.supportCoach, source: 'Staff', tone: 'good', icon: 'user-plus',
            });
        }
        for (const v of x.visits) {
            const s = P.SCHEDULE[v.session];
            const coach = /^Coach /.test(s.instructor) ? s.instructor : null;
            push({
                at: v.date, kind: 'attendance', title: s.name, detail: s.time + (coach ? ' · ' + coach : ' · Self-guided'),
                who: coach, source: 'Mindbody', tone: 'good', icon: 'check',
                payload: {
                    /* The unique index in MIGRATION.md §4 is (member_id, session_key, date). Same
                       person, same session, same day, once. */
                    sessionKey: String(v.session),
                    classId: classes[v.session].id,
                    className: s.name, time: s.time, coach, status: 'SignedIn',
                },
            });
        }
        for (const nn of x.notes) {
            push({ at: nn.at, kind: 'note', title: 'Note', detail: nn.text, who: nn.who, source: 'Staff', tone: 'plain', icon: 'note' });
        }
        for (const s of x.scans) {
            push({
                at: s.date, kind: 'inbody', title: 'InBody scan',
                detail: [s.weight != null ? s.weight + ' kg' : null, s.pbf != null ? s.pbf + '% body fat' : null, s.smm != null ? s.smm + ' kg muscle' : null].filter(Boolean).join(' · '),
                who: null, source: 'InBody', tone: 'info', icon: 'scan',
                payload: { weight: s.weight, pbf: s.pbf, smm: s.smm, score: s.score },
            });
        }
        for (const t of x.touches) {
            push({
                at: t.at, kind: t.kind,
                title: t.kind === 'sms' ? (t.inbound ? 'Reply' : 'Message') : 'Call',
                detail: t.text, who: t.by, source: t.source, tone: 'info',
                icon: t.kind === 'sms' ? 'message' : 'phone',
                ...(t.inbound ? { payload: { inbound: true } } : {}),
            });
        }
        if (m.suppression) {
            push({
                /* The seed always records all four. A suppression IMPORTED from a system that
                   never captured a reason or a review date has nulls, and the honest line then
                   says so rather than printing "null. Review null." A hold with no review date
                   is a member who can be forgotten: say it out loud. */
                at: m.suppression.declaredAt ?? m.joinedAt ?? clock.today(), kind: 'care',
                title: m.suppression.kind === 'care' ? 'Care Track' : 'Hold',
                detail: (m.suppression.reason ?? 'Reason not recorded')
                    + (m.suppression.reviewAt ? '. Review ' + m.suppression.reviewAt + '.'
                        : '. No review date on record.'),
                who: m.suppression.declaredBy, source: 'Staff', tone: 'warn', icon: 'shield',
            });
        }
        if (x.signal) {
            push({ at: x.signal.at, kind: 'signal', title: x.signal.title, detail: x.signal.detail, who: null, source: 'System', tone: 'risk', icon: 'alert' });
        }
        if (x.followUp) {
            const f = x.followUp;
            push({
                at: f.due, kind: 'followup', title: f.action, detail: f.why + '. ' + f.happened,
                who: f.owner, source: 'Follow-up', tone: 'warn', icon: 'flag',
                payload: { followUpId: f.id, status: f.status },
            });
            if (f.outcome && f.outcomeAt) {
                push({
                    at: f.outcomeAt, kind: 'outcome', title: f.outcome, detail: f.outcomeDetail ?? '',
                    who: f.owner, source: 'Follow-up', tone: f.status === 'completed' ? 'good' : 'info', icon: 'reply',
                    payload: { followUpId: f.id },
                });
            }
        }
        if (m.status === 'cancelled' && m.cancelledAt) {
            push({ at: m.cancelledAt, kind: 'cancelled', title: 'Membership ended', detail: String(m.sf['legacyStatus'] ?? 'Cancelled'), who: null, source: 'Mindbody', tone: 'risk', icon: 'x' });
        }
        /* MACRO TARGETS were worked out before attendance was counted, so their
           activity level is filled in here, off the same events, and the setting of
           them lands on the timeline like any other thing a coach did. */
        if (m.macros) {
            const t = m.macros;
            const cut = daysAgo(30);
            const inputs = {
                weightKg: t.weightKg, bodyFatPct: t.bodyFatPct, goal: t.goal,
                proteinPerLb: t.proteinPerLb, fatPct: t.fatPct,
                activity: activityFor(own.filter((e) => e.kind === 'attendance' && e.at >= cut).length),
            };
            const res = computeMacros(inputs);
            if (!('missing' in res))
                m.macros = toTargets(inputs, res, { setAt: t.setAt, by: t.by, scanDate: t.scanDate });
            push({
                at: t.setAt, kind: 'macros', title: 'Macro targets set', detail: macroSummary(m.macros),
                who: t.by, source: 'Staff', tone: 'info', icon: 'dashboard',
            });
        }
        sortStream(own, rank);
        own.forEach((e, i) => { e.id = m.id + ':' + i; e.payload = e.payload ?? {}; });
        /* Counted off the events, so a duplicate cannot inflate it. That inflation fed
           health, which feeds the number the roster is ranked by. */
        const cutoff = daysAgo(30);
        m.visits30d = own.filter((e) => e.kind === 'attendance' && e.at >= cutoff).length;
        events.push(...own);
    }
    for (const l of leads) {
        if (l.memberId)
            continue; // a converted lead's history lives on the member
        const own = [];
        const push = (e) => own.push({ payload: {}, ...e, id: '', memberId: null, leadId: l.id });
        for (const s of l.stageHistory) {
            const kind = s.stage === 'new' ? 'lead_created' : s.stage === 'contacted' ? 'contacted' : 'trial_attended';
            push({
                at: s.at, kind,
                title: s.stage === 'new' ? 'Enquiry' : s.stage === 'contacted' ? 'Contacted' : 'Stage: ' + s.stage,
                detail: s.stage === 'new' ? [l.source, l.sourceDetail].filter(Boolean).join(' · ') + (l.why ? ' · ' + l.why : '') : (l.nextAction ?? ''),
                who: s.stage === 'new' ? null : l.owner,
                source: s.stage === 'new' ? 'Enquiry' : 'Staff',
                tone: s.stage === 'lost' ? 'risk' : 'info', icon: s.stage === 'new' ? 'spark' : 'phone',
            });
        }
        for (const nn of l.notes) {
            push({ at: nn.at, kind: 'note', title: 'Note', detail: nn.text, who: nn.who, source: 'Staff', tone: 'plain', icon: 'note' });
        }
        const f = leadFollowUps.get(l.id);
        if (f) {
            push({
                at: f.due, kind: 'followup', title: f.action, detail: f.why + '. ' + f.happened,
                who: f.owner, source: 'Follow-up', tone: 'warn', icon: 'flag',
                payload: { followUpId: f.id, status: f.status },
            });
            if (f.outcome && f.outcomeAt) {
                push({
                    at: f.outcomeAt, kind: 'outcome', title: f.outcome, detail: f.outcomeDetail ?? '',
                    who: f.owner, source: 'Follow-up', tone: 'info', icon: 'reply',
                    payload: { followUpId: f.id },
                });
            }
        }
        sortStream(own, rank);
        own.forEach((e, i) => { e.id = l.id + ':' + i; e.payload = e.payload ?? {}; });
        events.push(...own);
    }
    /* ── The four demo seats ──────────────────────────────────────────────────────
       Gym-neutral only: the demo is walked through by prospects and the Accounts screen
       lists whatever is here, so an Acrostic-branded account would show our internal
       seats inside someone else's gym. Listed in PHASE order, because the login screen
       renders them in this order and the first seat a visitor tries should be the one
       Phase 1 actually ships.
  
       Each id is staffId(name), the same function the records use, so Coach Dana R.'s
       login id IS the supportCoachId on her members. Nothing has to be wired up. */
    const users = [
        { id: staffId('Coach Dana R.'), gymId: GYM_ID, name: 'Coach Dana R.', email: 'support@yourgym.com', roles: ['sales'], photoUrl: 'assets/people/staff-dana.jpg' },
        { id: staffId('Coach Marcus J.'), gymId: GYM_ID, name: 'Coach Marcus J.', email: 'coach@yourgym.com', roles: ['coach'], photoUrl: 'assets/people/staff-marcus.jpg' },
        { id: staffId('Nina Alvarez'), gymId: GYM_ID, name: 'Nina Alvarez', email: 'manager@yourgym.com', roles: ['manager'], photoUrl: 'assets/people/staff-nina.jpg' },
        { id: staffId('Simone Okafor'), gymId: GYM_ID, name: 'Simone Okafor', email: 'owner@yourgym.com', roles: ['gym_admin'], photoUrl: 'assets/people/staff-simone.jpg' },
    ];
    /* The whole roster, not just the four logins. Every name the records already
       reference gets a row, so a screen that has to offer "assign to somebody" has a
       list to offer rather than four accounts and a text box.
  
       WHO IS AN ACCOUNT IS A SEPARATE FACT from who is on the roster: the four demo
       logins carry their roles, everybody else carries none and cannot sign in. */
    const roster = [
        ...P.COACHES.map((name) => ({ kind: 'instructor', name })),
        ...P.SUPPORT_ROSTER.map((name) => ({ kind: 'support', name })),
        ...P.FRONT_DESK.map((name) => ({ kind: 'desk', name })),
    ];
    /* An account that is on no working roster is still somebody who works here. The
       owner teaches nothing and calls nobody back, and leaving her off meant the staff
       screens could show three of the four people who can sign in. */
    for (const u of users) {
        if (!roster.some((r) => r.name === u.name))
            roster.push({ kind: 'admin', name: u.name });
    }
    const staff = roster.map(({ kind, name }) => {
        const account = users.find((u) => u.name === name);
        return {
            id: staffId(name), gymId: GYM_ID, name, kind, active: true,
            email: account?.email ?? null,
            photoUrl: account?.photoUrl ?? null,
            roles: account?.roles ?? [],
        };
    });
    const gym = {
        id: GYM_ID, name: 'Your Gym', location: 'San Jose, CA',
        settings: {
            emailSending: false, smsSending: false,
            plan: 'Premium', memberTarget: 260,
            automations: { lead60sResponse: true, day2MissedWorkout: true, onboarding90d: true, winback90d: true },
            supportRoster: [...P.SUPPORT_ROSTER],
        },
    };
    /* The stored score is a CACHE of shared/model/risk.ts, never a second implementation
       (MIGRATION.md §5). Null when suppressed: the partial index makes "never rank a
       suppressed member" a property of the schema, and this is the same rule in the seed. */
    for (const m of members) {
        /* Cancellation initiated carries no score either, and for the opposite reason
           to suppression: not because they are off every list but because they are
           pinned to the top of one and the number is not why (PRD 4). */
        m.risk = isScored(m, today)
            ? riskModel(m, { today, model: tenantModel, unsourced: tenantUnsourced })
            : null;
    }
    const suspended = members.filter((m) => m.status === 'suspended').length;
    const active = members.filter((m) => m.status === 'active').length;
    const cancelled = members.filter((m) => m.status === 'cancelled').length;
    return {
        gym, users, staff, members, leads, classes, campaigns, followUps, approvals, events,
        meta: {
            seed, now: clock.nowMs, today, requested: want, riskModel: tenantModel,
            unsourced: tenantUnsourced,
            counts: {
                members: members.length, current: active + suspended, active, suspended, cancelled,
                suppressed: members.filter((m) => m.suppression).length,
                leads: leads.length, events: events.length, followUps: followUps.length,
            },
        },
    };
    function sortStream(list, order) {
        list.forEach((e, i) => { e._i = i; });
        list.sort((a, b) => {
            if (a.at !== b.at)
                return a.at < b.at ? -1 : 1;
            const r = (order[a.kind] ?? 99) - (order[b.kind] ?? 99);
            if (r)
                return r;
            return a._i - b._i;
        });
        for (const e of list)
            delete e._i;
    }
}
/* `?members=N`, parsed once, here, rather than in whichever screen happens to mount
   first. The default is the shipped demo's 287 and a bigger roster is asked for
   EXPLICITLY, because the real tenants are bigger than the demo: FNS is 2,951 records.
   Takes a query string rather than reading `location`, so this file stays DOM-free. */
export function membersFromSearch(search) {
    if (!search)
        return undefined;
    const raw = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get('members');
    if (raw == null)
        return undefined;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : undefined;
}
